import React, { useReducer } from 'react';
import PropTypes from 'prop-types';
import Avatar from '@strava/ui/Avatar';
import { isEmpty } from 'lodash-es';
import ActivityIcon from 'components/ActivityIcon';
import I18n from 'utils/I18n';
import ActionsStarHighlightedXsmall from '@strava/icons/ActionsStarHighlightedXsmall';
import SvgActionsLockClosedNormalXsmall from '@strava/icons/ActionsLockClosedNormalXsmall';
import SvgNavigationGroupNormalXsmall from '@strava/icons/NavigationGroupNormalXsmall';
import styles from './styles.scss';
import LocationAndTime from '../shared/LocationAndTime';
import ActivityEntryBody from '../shared/ActivityEntryBody';
import MaharajAchievementCelebration from '../shared/MaharajAchievementCelebration';
import PhotosAndMapImage from '../shared/PhotosAndMapImage';
import SocialShareDropdownActions from '../shared/SocialShareDropdownActions';
import useFeedAnalytics from '../../hooks/useFeedAnalytics';
import KudosAndComments from '../shared/KudosAndComments';
import {
  segAndBestEffortAchievementsProps,
  cumulativeGoalAchievementProps,
  kudosAndCommentsProps,
  feedTypeProps,
  mapProps,
  pageProps,
  photoListProps,
  powerAndSegmentGoalAchievementsProps,
  statsProps,
  viewingAthleteProps
} from '../../props/feed-prop-types';
import FeedEntityContext from '../../state/FeedEntityContext';
import { init, reducer } from '../../state/feed-entity-reducer';

const Activity = ({
  index,
  entity,
  activity,
  cursorData,
  page,
  feedType,
  viewingAthlete
}) => {
  const {
    displayDate,
    displayDateAtTime,
    location,
    timestampFormat
  } = activity.timeAndLocation;

  const {
    avatarUrl,
    athleteName,
    athleteId,
    memberType,
    sex,
    firstName
  } = activity.athlete;

  const {
    activityName,
    id: activityId,
    flagged,
    ownedByCurrentAthlete,
    segAndBestEffortAchievements,
    powerAndSegmentGoalAchievements,
    cumulativeGoalAchievement,
    type: activityType,
    stats,
    description,
    maharajAchievement,
    isVirtual,
    isCommute,
    workoutType,
    shareable,
    twitterUrl,
    isBoosted,
    visibility,
    mapAndPhotos
  } = activity;

  const { analyticsContext, intersectionObserverRef } = useFeedAnalytics({
    page,
    entity,
    activityId,
    feedType,
    cursorData
  });

  const initialState = {
    openKudosCommentModal: false,
    selectedIndex: 0,
    kudoPosted: false,
    newComment: null,
    analyticsContext
  };
  const [entityState, dispatch] = useReducer(reducer, initialState, init);
  const providerState = {
    entityState,
    dispatch
  };
  const cardMargin = index > 0 ? styles.cardMargin : '';
  const translatedActivityType = I18n.t(
    `strava.activities.types_v2.${activityType}`
  );

  const buildEntryHeader = () => (
    <>
      <div className={styles.entryHeader}>
        <div className={`${styles.media} ${styles.mediaMiddle} `}>
          <div
            className={`${styles.mediaLeft} ${styles.avatar}`}
            data-testid="avatar"
          >
            <Avatar
              src={avatarUrl}
              type="athlete"
              name={athleteName}
              href={`/athletes/${athleteId}`}
              badge={memberType}
            />
          </div>
          <div
            className={`${styles.mediaBody}`}
            data-testid="name-and-location"
          >
            <a href={`/athletes/${athleteId}`} data-testid="owners-name">
              {athleteName}
            </a>
            <LocationAndTime
              displayDate={displayDate}
              displayDateAtTime={displayDateAtTime}
              location={location}
              timestampFormat={timestampFormat}
            />
          </div>
          <div
            className={`${styles.mediaActions} ${styles.pending}`}
            data-testid="share"
          >
            <SocialShareDropdownActions
              activityId={activityId}
              shareable={shareable}
              twitterUrl={twitterUrl}
            />
            {isBoosted === true && (
              <div data-testid="boosted">
                <ActionsStarHighlightedXsmall fill="#b2afc0" />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );

  const buildEntryBody = () => {
    const elements = [];
    // activity icon
    elements.push(
      <div
        key="activity_icon"
        className={`${styles.entryIcon} ${styles.mediaLeft}`}
      >
        <ActivityIcon
          type={activityType}
          size="small"
          className={styles.activityIconImage}
          title={translatedActivityType}
        />
      </div>
    );
    // activity title, description, stats and acheivements
    elements.push(
      <div key="activity_entry_body" className={`${styles.mediaBody}`}>
        <ActivityEntryBody
          stats={stats}
          description={description}
          flagged={flagged}
          activityName={activityName}
          activityId={activityId}
          ownedByCurrentAthlete={ownedByCurrentAthlete}
          segAndBestEffortAchievements={segAndBestEffortAchievements}
          powerAndSegmentGoalAchievements={powerAndSegmentGoalAchievements}
          cumulativeGoalAchievement={cumulativeGoalAchievement}
          activityType={activityType}
          athleteSex={sex}
          athleteFirstName={firstName}
        />
      </div>
    );
    return elements;
  };

  const visibilityMessage = () => {
    let icon;
    let message;
    if (visibility === 'only_me') {
      icon = (
        <span data-testid="lock">
          <SvgActionsLockClosedNormalXsmall />
        </span>
      );
      message = (
        <span data-testid="only_me_text">
          {I18n.t('components.feed.module.private_activity_not_visible_html')}
        </span>
      );
    } else {
      icon = (
        <span data-testid="group">
          <SvgNavigationGroupNormalXsmall />
        </span>
      );
      message = (
        <span data-testid="group_text">
          {I18n.t('components.feed.module.followers_activity_not_visible_html')}
        </span>
      );
    }
    return (
      <div
        className={`${styles.visibilitySection}`}
        data-testid="visibility_section"
      >
        <div className={`${styles.appIconWrapper} ${styles.iconWrapper}`}>
          <div className={`${styles.visibilityIcon}`}>{icon}</div>
        </div>
        {message}
      </div>
    );
  };

  const entityOwner = {
    displayImage: avatarUrl,
    name: athleteName,
    memberType,
    profileUrl: `/athletes/${athleteId}`,
    type: 'athlete'
  };

  const buildEntryFooter = () => (
    <div className={styles.entryFooter}>
      {ownedByCurrentAthlete &&
        visibility !== 'everyone' &&
        visibilityMessage()}
      <KudosAndComments
        kudosAndComments={activity.kudosAndComments}
        entity={entity}
        entityId={activityId}
        ownedByCurrentAthlete={ownedByCurrentAthlete}
        viewingAthlete={viewingAthlete}
        entityOwner={entityOwner}
        entityTitle={activityName}
        openCommentsModalOnIconClick={false}
        segAndBestEffortAchievements={segAndBestEffortAchievements}
      />
    </div>
  );

  return (
    <FeedEntityContext.Provider value={providerState}>
      <div
        className={`web-feed-4-entry ${styles.feedEntry} ${styles.card} ${cardMargin}`}
        data-updated-at={cursorData.updated_at}
        data-rank={cursorData.rank}
        data-testid="web-feed-entry"
        ref={intersectionObserverRef}
      >
        {/* Entry Header - avatar, location, social actions */}
        {buildEntryHeader()}

        {/* Entry Body =  activity title, description, stats and achievements  */}
        <div className={styles.entryBody}>
          <div className={`${styles.media}`}>{buildEntryBody()}</div>
        </div>

        {/* Maharaj achievements */}
        {!isEmpty(maharajAchievement) && (
          <div className={styles.entryMedia}>
            <MaharajAchievementCelebration
              image={maharajAchievement.image}
              image_caption={maharajAchievement.image_caption}
              subtitle={maharajAchievement.subtitle}
              text={maharajAchievement.text}
              url={maharajAchievement.url}
            />
          </div>
        )}

        {/* Map and Photos */}
        {!isEmpty(mapAndPhotos) && (
          <div className={styles.entryMedia}>
            <PhotosAndMapImage
              activityId={activityId}
              activityMap={mapAndPhotos.activityMap}
              entryType={entity.toLowerCase()}
              isRoutable={mapAndPhotos.isRoutable}
              photoList={mapAndPhotos.photoList}
              feedName={page}
              isVirtual={isVirtual}
              isCommute={isCommute}
              workoutType={workoutType}
            />
          </div>
        )}

        {/* Entry Footer - Visibility Message , Kudos & Comments */}
        {buildEntryFooter()}
      </div>
    </FeedEntityContext.Provider>
  );
};

Activity.propTypes = {
  // feed entry types could be Activity, group_activity, Post etc
  entity: PropTypes.oneOf(['Activity']).isRequired,
  // range of number 0...19 representing rows returned by the server per feed load
  index: PropTypes.oneOf([...Array(20).keys()]).isRequired,
  activity: PropTypes.shape({
    activityName: PropTypes.string,
    id: PropTypes.string,
    flagged: PropTypes.bool,
    ownedByCurrentAthlete: PropTypes.bool,
    segAndBestEffortAchievements: segAndBestEffortAchievementsProps,
    powerAndSegmentGoalAchievements: powerAndSegmentGoalAchievementsProps,
    cumulativeGoalAchievement: cumulativeGoalAchievementProps,
    type: PropTypes.string,
    stats: statsProps,
    description: PropTypes.string,
    maharajAchievement: PropTypes.shape({
      image: PropTypes.string,
      image_caption: PropTypes.string,
      subtitle: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string
    }),
    isVirtual: PropTypes.bool.isRequired,
    isCommute: PropTypes.bool.isRequired,
    workoutType: PropTypes.string,
    shareable: PropTypes.bool.isRequired,
    twitterUrl: PropTypes.string.isRequired,
    isBoosted: PropTypes.bool,
    visibility: PropTypes.string.isRequired,
    athlete: PropTypes.shape({
      avatarUrl: PropTypes.string,
      athleteId: PropTypes.string.isRequired,
      memberType: PropTypes.string,
      athleteName: PropTypes.string.isRequired,
      sex: PropTypes.string,
      firstName: PropTypes.string.isRequired
    }).isRequired,
    mapAndPhotos: PropTypes.shape({
      activityMap: mapProps,
      isRoutable: PropTypes.bool,
      photoList: photoListProps
    }).isRequired,
    timeAndLocation: PropTypes.shape({
      displayDate: PropTypes.string,
      displayDateAtTime: PropTypes.string,
      location: PropTypes.string,
      timestampFormat: PropTypes.string
    }).isRequired,
    kudosAndComments: kudosAndCommentsProps.isRequired
  }).isRequired,
  cursorData: PropTypes.shape({
    updated_at: PropTypes.number.isRequired,
    rank: PropTypes.number // can be null when viewing feed type with a filter on athlete id
  }).isRequired,
  page: pageProps.isRequired,
  feedType: feedTypeProps.isRequired,
  viewingAthlete: viewingAthleteProps.isRequired
};

export default Activity;
